import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  getAuth
} from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import { auth, db, firebaseConfig } from '../firebaseConfig';
import { useAuthState } from 'react-firebase-hooks/auth';
import Sidebar from './Sidebar';
import UserTable from './UserTable';
import '../App.css';
import '../responsive.css';
import './Photos.css';

const AdminDashboardPage = () => {
  const [user] = useAuthState(auth);
  const [userName, setUserName] = useState('');
  const [role, setRole] = useState('');
  const [loadingRole, setLoadingRole] = useState(true);

  const [availableUsers, setAvailableUsers] = useState([]);

  // Registration form
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regCompany, setRegCompany] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regRole, setRegRole] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [registerError, setRegisterError] = useState('');

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.name) setUserName(data.name);
          if (data.role) setRole(data.role);
        }
      }
      setLoadingRole(false);
    };
    fetchCurrentUser();
  }, [user]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      const snapshot = await getDocs(collection(db, 'users'));
      const list = [];
      snapshot.forEach(doc => {
        const { name, email, role, createdAt } = doc.data();
        const uid = doc.id;
        list.push({
          uid,
          name: name || '',
          email: email || '',
          role: role || '—',
          joined: createdAt
            ? new Date(createdAt.seconds * 1000).toLocaleDateString()
            : '—'
        });
      });
      setAvailableUsers(list);
    };

    fetchAllUsers();
  }, []);

  const registerNewUser = async () => {
    setRegisterError('');
    setRegisterSuccess('');

    if (!regEmail || !regPassword || !regFirstName || !regRole) {
      setRegisterError('Please fill in all required fields.');
      return;
    }

    const secondaryApp = initializeApp(firebaseConfig, 'SecondaryApp');
    const secondaryAuth = getAuth(secondaryApp);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        regEmail,
        regPassword
      );
      const newUser = userCredential.user;

      await setDoc(doc(db, 'users', newUser.uid), {
        email: regEmail,
        name: `${regFirstName} ${regLastName}`.trim(),
        firstName: regFirstName,
        lastName: regLastName,
        company: regCompany,
        phoneNumber: regPhone,
        role: regRole,
        createdAt: new Date()
      });

      setRegisterSuccess(`User ${regFirstName} ${regLastName} registered successfully.`);
      setRegEmail('');
      setRegPassword('');
      setRegFirstName('');
      setRegLastName('');
      setRegCompany('');
      setRegPhone('');
      setRegRole('');
    } catch (err) {
      console.error('Registration failed:', err);
      setRegisterError(err.message || 'Failed to register user.');
    } finally {
      await deleteApp(secondaryApp);
    }
  };

  if (loadingRole) return <p style={{ padding: '2rem' }}>Loading...</p>;
  if (role !== 'admin') return <p style={{ padding: '2rem' }}>Access denied: Admins only.</p>;

  return (
    <div className="AdminDashboardPage">
      <header>Admin Console</header>

      <Sidebar role={role} buttons={[]} />

      <div
        className="main-content"
        style={{
          color: 'black',
          marginLeft: '200px',
          padding: '2rem 2rem 2rem 50px',
          maxWidth: '1000px'
        }}
      >
        {userName && (
          <h2 style={{ fontSize: '1.5rem', marginTop: '1rem' }}>
            Welcome {userName}
          </h2>
        )}

        {/* ✅ Modern User Table */}
        <UserTable
          users={availableUsers}
          onInvite={() => {
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          }}
        />

        <hr style={{ margin: '3rem 0' }} />

        <h3>Register New User</h3>

        {registerSuccess && <p style={{ color: 'green' }}>{registerSuccess}</p>}
        {registerError && <p style={{ color: 'red' }}>{registerError}</p>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <input type="text" value={regFirstName} onChange={e => setRegFirstName(e.target.value)} placeholder="First Name" />
          <input type="text" value={regLastName} onChange={e => setRegLastName(e.target.value)} placeholder="Last Name" />
          <input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="Email" />
          <input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Password" />
          <input type="text" value={regCompany} onChange={e => setRegCompany(e.target.value)} placeholder="Company" />
          <input type="text" value={regPhone} onChange={e => setRegPhone(e.target.value)} placeholder="Phone Number" />

          <div>
            <label><strong>Select Role:</strong></label>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
              <button
                onClick={() => setRegRole('Engineer')}
                style={{
                  backgroundColor: regRole === 'Engineer' ? '#007bff' : '#ccc',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Engineer
              </button>
              <button
                onClick={() => setRegRole('Building Owner/PM')}
                style={{
                  backgroundColor: regRole === 'Building Owner/PM' ? '#007bff' : '#ccc',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Building Owner/PM
              </button>
            </div>
          </div>

          <button
            onClick={registerNewUser}
            style={{
              marginTop: '1rem',
              padding: '0.75rem',
              backgroundColor: 'green',
              color: 'white',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ✅ Register User
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
